/** Reprice-vs-current-catalog dialog (spec §2.5). Controlled: parent owns the
 *  diff and the accept/cancel outcomes. Same visual language as
 *  ExecConfirmDialog (toast idiom).
 *
 *  Under an integration-% override the session prices the ENTIRE catalog, so
 *  this table can run to dozens of rows. Only the row region scrolls — the
 *  basis panel and the accept/cancel controls are pinned outside it, so the
 *  rebase always stays confirmable no matter how long the diff is. */
import React, { useEffect, useMemo, useRef } from 'react';
import { buildRepriceView } from '../pricing/repriceView';

const PANEL_BG = '#1a1500';

const fmt = (n) => (n === null || n === undefined ? '—'
  : `${n < 0 ? '-' : ''}$${Math.abs(Number(n)).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtSigned = (n) => (n > 0 ? `+${fmt(n)}` : fmt(n));
const pct = (bp) => `${(bp / 100).toFixed(2)}%`;

export default function RepriceDialog({ diff, qtyByPn, basis, onAccept, onCancel }) {
  const dialogRef = useRef(null);
  const previousFocusRef = useRef(null);
  // Ref-mirrored so the mount-only effect below always calls the CURRENT
  // onCancel without re-running focus/listener setup on every prop change.
  const onCancelRef = useRef(onCancel);
  onCancelRef.current = onCancel;

  useEffect(() => {
    previousFocusRef.current = document.activeElement;
    dialogRef.current?.querySelector('button')?.focus();
    const onKeyDown = (e) => { if (e.key === 'Escape') onCancelRef.current(); };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      previousFocusRef.current?.focus?.();
    };
  }, []);

  const view = useMemo(() => buildRepriceView(diff, qtyByPn || {}), [diff, qtyByPn]);
  const integrationOverridden = basis?.overrideIntegrationBp !== null
    && basis?.overrideIntegrationBp !== undefined;
  const effectiveBp = integrationOverridden
    ? basis.overrideIntegrationBp : basis?.catalogIntegrationBp;

  return (
    <div ref={dialogRef} className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" role="dialog"
         aria-modal="true" aria-labelledby="reprice-dialog-title">
      <div className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded border-t-4 p-4"
           style={{ background: PANEL_BG, borderTopColor: '#E79F23' }}>
        <span className="mb-2 inline-block self-start rounded-full bg-amber-500/20 px-2 py-0.5 text-xs font-bold text-amber-400">
          EXEC PRICING
        </span>
        <h2 id="reprice-dialog-title" className="mb-2 font-semibold text-gray-100">
          Reprice against current catalog
        </h2>

        {/* Session basis — what this rebase is being measured against. */}
        <div data-testid="reprice-basis"
             className="mb-3 rounded border border-gray-700 px-3 py-2 text-xs text-gray-300">
          <div className="mb-1 font-semibold uppercase tracking-wider text-gray-500">Session basis</div>
          <div>
            Vendor <span className="text-gray-100">{basis?.vendor ?? '—'}</span>
            {' · '}Quarter <span className="text-gray-100">{basis?.quarter ?? '—'}</span>
            {' · '}Integration markup{' '}
            <span className="text-gray-100">
              {effectiveBp === null || effectiveBp === undefined ? '—' : pct(effectiveBp)}
            </span>
            {integrationOverridden && (
              <span className="text-amber-400">
                {' '}(override; catalog {pct(basis.catalogIntegrationBp)})
              </span>
            )}
          </div>
          <div>
            Custom-priced SKUs:{' '}
            <span className="text-gray-100">{basis?.customSkuCount ?? view.totalCount}</span>
            {' of '}{basis?.catalogSkuCount ?? view.totalCount}
          </div>
        </div>

        {diff.identical ? (
          <p className="mb-4 text-sm text-gray-300">
            Catalog unchanged since this basis was frozen — no rebase required.
          </p>
        ) : (
          <>
            <p className="mb-2 text-sm text-yellow-300">
              {diff.changedComponentCount} catalog value{diff.changedComponentCount === 1 ? '' : 's'} changed
              since this session&rsquo;s basis was frozen. Executive overrides are preserved and
              re-applied to the new basis.
            </p>

            {integrationOverridden && (
              <p data-testid="reprice-scope-notice"
                 className="mb-2 rounded border border-amber-600/50 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                Integration markup override in effect — all {basis?.catalogSkuCount ?? view.totalCount} catalog
                SKUs are priced under this session and are therefore in scope for this rebase. Lines the
                current BOM does not carry are repriced but have no quote impact.
              </p>
            )}

            <div data-testid="reprice-summary" className="mb-2 text-xs text-gray-400">
              <span className="text-gray-200">{view.changedCount}</span> of {view.totalCount} lines repriced
              {' · '}net quote impact{' '}
              <span className={view.netQuoteImpact === 0 ? 'text-gray-200' : 'text-amber-300'}>
                {fmtSigned(view.netQuoteImpact)}
              </span>
            </div>

            {/* Only this region scrolls: the controls below stay reachable. */}
            <div data-testid="reprice-scroll"
                 className="mb-4 min-h-0 flex-1 overflow-y-auto rounded border border-gray-700">
              <table className="w-full text-sm text-gray-200">
                <thead className="sticky top-0 text-left text-gray-400" style={{ background: PANEL_BG }}>
                  <tr>
                    <th className="px-2 py-1">SKU</th>
                    <th className="px-2 py-1 text-right">Current</th>
                    <th className="px-2 py-1 text-right">Repriced</th>
                    <th className="px-2 py-1 text-right">Δ/unit</th>
                    <th className="px-2 py-1 text-right">Qty</th>
                    <th className="px-2 py-1 text-right">Δ total</th>
                  </tr>
                </thead>
                <tbody>
                  {view.rows.map((r) => (
                    <tr key={r.productSku}
                        className={`border-t border-gray-800 ${r.changed ? 'text-amber-300' : ''}`}>
                      <td className="px-2 py-1">{r.productSku}</td>
                      <td className="px-2 py-1 text-right">{fmt(r.oldPrice)}</td>
                      <td className="px-2 py-1 text-right">{fmt(r.newPrice)}</td>
                      <td className="px-2 py-1 text-right">
                        {r.newPrice === null ? 'no longer priceable' : fmt(r.deltaPerUnit)}
                      </td>
                      <td className="px-2 py-1 text-right">{r.qty === null ? '—' : r.qty}</td>
                      <td className="px-2 py-1 text-right">{fmt(r.deltaExtended)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel}
                  className="rounded border border-gray-600 px-3 py-1 text-sm text-gray-200">Cancel</button>
          {!diff.identical && (
            <button type="button" onClick={onAccept}
                    className="rounded bg-amber-500 px-3 py-1 text-sm font-semibold text-black">
              Accept new basis
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
